'use client'
import React, { useState, useEffect, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import toast from 'react-hot-toast'
import { X } from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

interface ExportDecisionModalProps {
    isOpen: boolean
    onClose: () => void
    onExport: (data: any) => Promise<void>
    courseId: string
    isLoading?: boolean
}

const DECISION_TYPES = [
    { value: 0, label: 'Tất cả' },
	{ value: 7, label: '0. Báo cáo đăng ký khóa đào tạo' },
    { value: 1, label: '1. Quyết định mở lớp' },
    { value: 2, label: '2. Quyết định thành lập HĐ xét TN' },
    { value: 3, label: '3. Quyết định thành lập HĐ thi TN' },
    { value: 4, label: '4. Quyết định công nhận TN' },
    { value: 5, label: '5. Phân công chấm thi' },
    { value: 6, label: '6. Biên bản tổng hợp TN' },
    { value: 8, label: 'Danh sách tổng hợp' },
]


export default function ExportDecisionModal({
    isOpen,
    onClose,
    onExport,
    courseId,
    isLoading = false
}: ExportDecisionModalProps) {
    const [decisionType, setDecisionType] = useState<number>(0)
    const [reportNumber, setReportNumber] = useState<string>('')
    const [reportDate, setReportDate] = useState<Date | null>(null)
    const [soQuyetDinh1, setSoQuyetDinh1] = useState<string>('')
    const [soQuyetDinh2, setSoQuyetDinh2] = useState<string>('')
    const [soQuyetDinh3, setSoQuyetDinh3] = useState<string>('')
    const [soQuyetDinh4, setSoQuyetDinh4] = useState<string>('')
    const [ngayQuyetDinh1, setNgayQuyetDinh1] = useState<Date | null>(null)
    const [ngayQuyetDinh2, setNgayQuyetDinh2] = useState<Date | null>(null)
    const [ngayQuyetDinh3, setNgayQuyetDinh3] = useState<Date | null>(null)
    const [ngayQuyetDinh4, setNgayQuyetDinh4] = useState<Date | null>(null)
    const [ngayPhanCong, setNgayPhanCong] = useState<Date | null>(null)
    const [ngayBienBanTH, setNgayBienBanTH] = useState<Date | null>(null)
    const [ngayThi, setNgayThi] = useState<Date | null>(null)
    const [soLuongHV, setSoLuongHV] = useState<number | null>(null)
    const [soLuongHVKT, setSoLuongHVKT] = useState<number | null>(null)

    // Reset all fields when decision type changes
    useEffect(() => {
        setReportNumber('')
        setReportDate(null)
        setSoQuyetDinh1('')
        setSoQuyetDinh2('')
        setSoQuyetDinh3('')
        setSoQuyetDinh4('')
        setNgayQuyetDinh1(null)
        setNgayQuyetDinh2(null)
        setNgayQuyetDinh3(null)
        setNgayQuyetDinh4(null)
        setNgayPhanCong(null)
        setNgayBienBanTH(null)
        setNgayThi(null)
        setSoLuongHV(null)
        setSoLuongHVKT(null)
    }, [decisionType])

    const handleSubmit = async () => {
        // Validation based on decision type
        if (decisionType === 7 && (!reportNumber || !reportDate)) {
            toast.error("Vui lòng nhập đầy đủ thông tin Số báo cáo và Ngày báo cáo")
            return
        }
        if (decisionType === 1 && (!soQuyetDinh1 || !ngayQuyetDinh1)) {
            toast.error("Vui lòng nhập đầy đủ thông tin Quyết định mở lớp")
            return
        }
        if (decisionType === 4 && (!soQuyetDinh4 || !ngayQuyetDinh4)) {
            toast.error("Vui lòng nhập đầy đủ thông tin Quyết định công nhận TN")
            return
        }
        if (decisionType === 5 && !ngayPhanCong) {
            toast.error("Vui lòng nhập Ngày phân công")
            return
        }
        if (decisionType === 6 && !ngayBienBanTH) {
            toast.error("Vui lòng nhập Ngày biên bản tổng hợp")
            return
        }

        try {
            const data: any = {
                decisionType,
                courseId
            }

            // Add fields based on decision type
            if (decisionType === 0 || decisionType === 7) {
                data.reportNumber = reportNumber
                data.reportDate = reportDate ? reportDate.toISOString() : null
            }
            if (decisionType === 0 || decisionType === 1) {
                data.soQuyetDinh1 = soQuyetDinh1
                data.ngayQuyetDinh1 = ngayQuyetDinh1 ? ngayQuyetDinh1.toISOString() : null
            }
            if (decisionType === 0 || decisionType === 2) {
                data.soQuyetDinh2 = soQuyetDinh2
                data.ngayQuyetDinh2 = ngayQuyetDinh2 ? ngayQuyetDinh2.toISOString() : null
            }
            if (decisionType === 0 || decisionType === 3) {
                data.soQuyetDinh3 = soQuyetDinh3
                data.ngayQuyetDinh3 = ngayQuyetDinh3 ? ngayQuyetDinh3.toISOString() : null
            }
            if (decisionType === 0 || decisionType === 4) {
                data.soQuyetDinh4 = soQuyetDinh4
                data.ngayQuyetDinh4 = ngayQuyetDinh4 ? ngayQuyetDinh4.toISOString() : null
            }
            // Add ngayThi for applicable decision types
            if (decisionType === 0 || decisionType === 3 || decisionType === 4 || decisionType === 8) {
                data.ngayThi = ngayThi ? new Date(ngayThi.getFullYear(), ngayThi.getMonth(), ngayThi.getDate(), 12, 0, 0).toISOString() : null
            }
            // For decision type 8 (Danh sách tổng hợp), only include dates, not numbers
            if (decisionType === 8) {
                data.ngayQuyetDinh1 = ngayQuyetDinh1 ? ngayQuyetDinh1.toISOString() : null
                data.ngayQuyetDinh2 = ngayQuyetDinh2 ? ngayQuyetDinh2.toISOString() : null
                data.ngayQuyetDinh3 = ngayQuyetDinh3 ? ngayQuyetDinh3.toISOString() : null
                data.ngayQuyetDinh4 = ngayQuyetDinh4 ? ngayQuyetDinh4.toISOString() : null
            }
            if (decisionType === 0 || decisionType === 5) {
                data.ngayPhanCong = ngayPhanCong ? ngayPhanCong.toISOString() : null
            }
            if (decisionType === 0 || decisionType === 6) {
                data.ngayBienBanTH = ngayBienBanTH ? ngayBienBanTH.toISOString() : null
            }

            // Add nullable student count fields - Only for decision types 2, 3, 4
            if (decisionType == 0 || decisionType === 2 || decisionType === 3 || decisionType === 4) {
                data.soLuongHV = soLuongHV
                data.soLuongHVKT = soLuongHVKT
            }

            await onExport(data)
            onClose()
            // Reset form
            setDecisionType(0)
        } catch (error) {
            console.error('Export failed:', error)
        }
    }

    const shouldShowField = (types: number[]) => {
        return decisionType === 0 || types.includes(decisionType)
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm"/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className="w-full max-w-2xl transform rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all max-h-[90vh] overflow-y-auto relative">
                                <div className="flex justify-between items-center mb-4">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900"
                                    >
                                        Xuất Quyết định
                                    </Dialog.Title>
                                    <button
                                        onClick={onClose}
                                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        <X/>
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {/* Decision Type - Always shown */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Loại quyết định <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={decisionType}
                                            onChange={(e) => setDecisionType(Number(e.target.value))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            {DECISION_TYPES.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Ngày thi - for applicable decision types */}
                                    {shouldShowField([3, 4, 8]) && (
                                        <>
                                            <div className="border-t pt-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Ngày thi
                                                    </label>
                                                    <DatePicker
                                                        selected={ngayThi}
                                                        onChange={(date) => setNgayThi(date)}
                                                        dateFormat="dd/MM/yyyy"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        wrapperClassName="w-full relative"
                                                        calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                        placeholderText="Chọn ngày"
                                                        calendarStartDay={1}
                                                        showPopperArrow={false}
                                                        popperClassName="!z-[9999]"
                                                        popperPlacement="bottom-start"
                                                        popperModifiers={[
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 8]
                                                                }
                                                            },
                                                            {
                                                                name: 'preventOverflow',
                                                                options: {
                                                                    rootBoundary: 'viewport',
                                                                    tether: false,
                                                                    altAxis: true
                                                                }
                                                            }
                                                        ]}
                                                        renderCustomHeader={({
                                                            date,
                                                            decreaseMonth,
                                                            increaseMonth,
                                                            prevMonthButtonDisabled,
                                                            nextMonthButtonDisabled,
                                                        }) => (
                                                            <div className="flex items-center justify-between px-2 py-2">
                                                                <button
                                                                    onClick={decreaseMonth}
                                                                    disabled={prevMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng trước"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>

                                                                <span className="text-sm font-semibold text-gray-800">
                                                                    {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                </span>

                                                                <button
                                                                    onClick={increaseMonth}
                                                                    disabled={nextMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng sau"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Student Count Fields - Only for decision types 2, 3, 4 */}
                                    {shouldShowField([2, 3, 4]) && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Số lượng học viên
                                                </label>
                                                <input
                                                    type="number"
                                                    value={soLuongHV ?? ''}
                                                    onChange={(e) => setSoLuongHV(e.target.value ? Number(e.target.value) : null)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="Nhập số lượng học viên"
                                                    min="0"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Số lượng học viên khóa trước
                                                </label>
                                                <input
                                                    type="number"
                                                    value={soLuongHVKT ?? ''}
                                                    onChange={(e) => setSoLuongHVKT(e.target.value ? Number(e.target.value) : null)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="Nhập số lượng học viên khóa trước"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Report fields - Decision type 7 */}
                                    {shouldShowField([7]) && (
                                        <>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Số báo cáo {decisionType === 7 && <span className="text-red-500">*</span>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportNumber}
                                                        onChange={(e) => setReportNumber(e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="VD: 123/BC"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Ngày báo cáo {decisionType === 7 && <span className="text-red-500">*</span>}
                                                    </label>
                                                    <DatePicker
                                                        selected={reportDate}
                                                        onChange={(date) => setReportDate(date)}
                                                        dateFormat="dd/MM/yyyy"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        wrapperClassName="w-full relative"
                                                        calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                        placeholderText="Chọn ngày"
                                                        calendarStartDay={1}
                                                        showPopperArrow={false}
                                                        popperClassName="!z-[9999]"
                                                        popperPlacement="bottom-start"
                                                        popperModifiers={[
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 8]
                                                                }
                                                            },
                                                            {
                                                                name: 'preventOverflow',
                                                                options: {
                                                                    rootBoundary: 'viewport',
                                                                    tether: false,
                                                                    altAxis: true
                                                                }
                                                            }
                                                        ]}
                                                        renderCustomHeader={({
                                                            date,
                                                            decreaseMonth,
                                                            increaseMonth,
                                                            prevMonthButtonDisabled,
                                                            nextMonthButtonDisabled,
                                                        }) => (
                                                            <div className="flex items-center justify-between px-2 py-2">
                                                                <button
                                                                    onClick={decreaseMonth}
                                                                    disabled={prevMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng trước"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>

                                                                <span className="text-sm font-semibold text-gray-800">
                                                                    {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                </span>

                                                                <button
                                                                    onClick={increaseMonth}
                                                                    disabled={nextMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng sau"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Decision 1 - Quyết định mở lớp */}
                                    {shouldShowField([1, 8]) && (
                                        <>
                                            <div className="border-t pt-4">
                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                    1. Quyết định mở lớp
                                                </h4>
                                                <div className={`grid ${decisionType === 8 ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                                                    {decisionType !== 8 && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Số quyết định {decisionType === 1 && <span className="text-red-500">*</span>}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={soQuyetDinh1}
                                                                onChange={(e) => setSoQuyetDinh1(e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Ngày quyết định {decisionType === 1 && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <DatePicker
                                                            selected={ngayQuyetDinh1}
                                                            onChange={(date) => setNgayQuyetDinh1(date)}
                                                            dateFormat="dd/MM/yyyy"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            wrapperClassName="w-full relative"
                                                            calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                            placeholderText="Chọn ngày"
                                                            calendarStartDay={1}
                                                            showPopperArrow={false}
                                                            popperClassName="!z-[9999]"
                                                            popperPlacement="bottom-start"
                                                            popperModifiers={[
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, 8]
                                                                    }
                                                                },
                                                                {
                                                                    name: 'preventOverflow',
                                                                    options: {
                                                                        rootBoundary: 'viewport',
                                                                        tether: false,
                                                                        altAxis: true
                                                                    }
                                                                }
                                                            ]}
                                                            renderCustomHeader={({
                                                                date,
                                                                decreaseMonth,
                                                                increaseMonth,
                                                                prevMonthButtonDisabled,
                                                                nextMonthButtonDisabled,
                                                            }) => (
                                                                <div className="flex items-center justify-between px-2 py-2">
                                                                    <button
                                                                        onClick={decreaseMonth}
                                                                        disabled={prevMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng trước"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>

                                                                    <span className="text-sm font-semibold text-gray-800">
                                                                        {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                    </span>

                                                                    <button
                                                                        onClick={increaseMonth}
                                                                        disabled={nextMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng sau"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Decision 2 - Quyết định thành lập HD xét TN */}
                                    {shouldShowField([2, 8]) && (
                                        <>
                                            <div className="border-t pt-4">
                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                    2. Quyết định thành lập HD xét TN
                                                </h4>
                                                <div className={`grid ${decisionType === 8 ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                                                    {decisionType !== 8 && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Số quyết định
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={soQuyetDinh2}
                                                                onChange={(e) => setSoQuyetDinh2(e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Ngày quyết định
                                                        </label>
                                                        <DatePicker
                                                            selected={ngayQuyetDinh2}
                                                            onChange={(date) => setNgayQuyetDinh2(date)}
                                                            dateFormat="dd/MM/yyyy"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            wrapperClassName="w-full relative"
                                                            calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                            placeholderText="Chọn ngày"
                                                            calendarStartDay={1}
                                                            showPopperArrow={false}
                                                            popperClassName="!z-[9999]"
                                                            popperPlacement="bottom-start"
                                                            popperModifiers={[
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, 8]
                                                                    }
                                                                },
                                                                {
                                                                    name: 'preventOverflow',
                                                                    options: {
                                                                        rootBoundary: 'viewport',
                                                                        tether: false,
                                                                        altAxis: true
                                                                    }
                                                                }
                                                            ]}
                                                            renderCustomHeader={({
                                                                date,
                                                                decreaseMonth,
                                                                increaseMonth,
                                                                prevMonthButtonDisabled,
                                                                nextMonthButtonDisabled,
                                                            }) => (
                                                                <div className="flex items-center justify-between px-2 py-2">
                                                                    <button
                                                                        onClick={decreaseMonth}
                                                                        disabled={prevMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng trước"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>

                                                                    <span className="text-sm font-semibold text-gray-800">
                                                                        {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                    </span>

                                                                    <button
                                                                        onClick={increaseMonth}
                                                                        disabled={nextMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng sau"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Decision 3 - Quyết định thành lập HD thi TN */}
                                    {shouldShowField([3, 8]) && (
                                        <>
                                            <div className="border-t pt-4">
                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                    3. Quyết định thành lập HD thi TN
                                                </h4>
                                                <div className={`grid ${decisionType === 8 ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                                                    {decisionType !== 8 && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Số quyết định
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={soQuyetDinh3}
                                                                onChange={(e) => setSoQuyetDinh3(e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Ngày quyết định
                                                        </label>
                                                        <DatePicker
                                                            selected={ngayQuyetDinh3}
                                                            onChange={(date) => setNgayQuyetDinh3(date)}
                                                            dateFormat="dd/MM/yyyy"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            wrapperClassName="w-full relative"
                                                            calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                            placeholderText="Chọn ngày"
                                                            calendarStartDay={1}
                                                            showPopperArrow={false}
                                                            popperClassName="!z-[9999]"
                                                            popperPlacement="bottom-start"
                                                            popperModifiers={[
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, 8]
                                                                    }
                                                                },
                                                                {
                                                                    name: 'preventOverflow',
                                                                    options: {
                                                                        rootBoundary: 'viewport',
                                                                        tether: false,
                                                                        altAxis: true
                                                                    }
                                                                }
                                                            ]}
                                                            renderCustomHeader={({
                                                                date,
                                                                decreaseMonth,
                                                                increaseMonth,
                                                                prevMonthButtonDisabled,
                                                                nextMonthButtonDisabled,
                                                            }) => (
                                                                <div className="flex items-center justify-between px-2 py-2">
                                                                    <button
                                                                        onClick={decreaseMonth}
                                                                        disabled={prevMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng trước"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>

                                                                    <span className="text-sm font-semibold text-gray-800">
                                                                        {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                    </span>

                                                                    <button
                                                                        onClick={increaseMonth}
                                                                        disabled={nextMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng sau"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Decision 4 - Quyết định công nhận TN */}
                                    {shouldShowField([4, 8]) && (
                                        <>
                                            <div className="border-t pt-4">
                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                    4. Quyết định công nhận TN
                                                </h4>
                                                <div className={`grid ${decisionType === 8 ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                                                    {decisionType !== 8 && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Số quyết định {decisionType === 4 && <span className="text-red-500">*</span>}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={soQuyetDinh4}
                                                                onChange={(e) => setSoQuyetDinh4(e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Ngày quyết định {decisionType === 4 && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <DatePicker
                                                            selected={ngayQuyetDinh4}
                                                            onChange={(date) => setNgayQuyetDinh4(date)}
                                                            dateFormat="dd/MM/yyyy"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            wrapperClassName="w-full relative"
                                                            calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                            placeholderText="Chọn ngày"
                                                            calendarStartDay={1}
                                                            showPopperArrow={false}
                                                            popperClassName="!z-[9999]"
                                                            popperPlacement="bottom-start"
                                                            popperModifiers={[
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, 8]
                                                                    }
                                                                },
                                                                {
                                                                    name: 'preventOverflow',
                                                                    options: {
                                                                        rootBoundary: 'viewport',
                                                                        tether: false,
                                                                        altAxis: true
                                                                    }
                                                                }
                                                            ]}
                                                            renderCustomHeader={({
                                                                date,
                                                                decreaseMonth,
                                                                increaseMonth,
                                                                prevMonthButtonDisabled,
                                                                nextMonthButtonDisabled,
                                                            }) => (
                                                                <div className="flex items-center justify-between px-2 py-2">
                                                                    <button
                                                                        onClick={decreaseMonth}
                                                                        disabled={prevMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng trước"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>

                                                                    <span className="text-sm font-semibold text-gray-800">
                                                                        {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                    </span>

                                                                    <button
                                                                        onClick={increaseMonth}
                                                                        disabled={nextMonthButtonDisabled}
                                                                        type="button"
                                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                            nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                        }`}
                                                                        aria-label="Tháng sau"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-5 w-5 text-gray-600"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Decision 5 - Phân công chấm thi */}
                                    {shouldShowField([5]) && (
                                        <>
                                            <div className="border-t pt-4">
                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                    5. Phân công chấm thi
                                                </h4>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Ngày phân công {decisionType === 5 && <span className="text-red-500">*</span>}
                                                    </label>
                                                    <DatePicker
                                                        selected={ngayPhanCong}
                                                        onChange={(date) => setNgayPhanCong(date)}
                                                        dateFormat="dd/MM/yyyy"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        wrapperClassName="w-full relative"
                                                        calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                        placeholderText="Chọn ngày"
                                                        calendarStartDay={1}
                                                        showPopperArrow={false}
                                                        popperClassName="!z-[9999]"
                                                        popperPlacement="bottom-start"
                                                        popperModifiers={[
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 8]
                                                                }
                                                            },
                                                            {
                                                                name: 'preventOverflow',
                                                                options: {
                                                                    rootBoundary: 'viewport',
                                                                    tether: false,
                                                                    altAxis: true
                                                                }
                                                            }
                                                        ]}
                                                        renderCustomHeader={({
                                                            date,
                                                            decreaseMonth,
                                                            increaseMonth,
                                                            prevMonthButtonDisabled,
                                                            nextMonthButtonDisabled,
                                                        }) => (
                                                            <div className="flex items-center justify-between px-2 py-2">
                                                                <button
                                                                    onClick={decreaseMonth}
                                                                    disabled={prevMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng trước"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>

                                                                <span className="text-sm font-semibold text-gray-800">
                                                                    {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                </span>

                                                                <button
                                                                    onClick={increaseMonth}
                                                                    disabled={nextMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng sau"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Decision 6 - Biên bản tổng hợp TN */}
                                    {shouldShowField([6]) && (
                                        <>
                                            <div className="border-t pt-4">
                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                    6. Biên bản tổng hợp TN
                                                </h4>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Ngày biên bản tổng hợp {decisionType === 6 && <span className="text-red-500">*</span>}
                                                    </label>
                                                    <DatePicker
                                                        selected={ngayBienBanTH}
                                                        onChange={(date) => setNgayBienBanTH(date)}
                                                        dateFormat="dd/MM/yyyy"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        wrapperClassName="w-full relative"
                                                        calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                                        placeholderText="Chọn ngày"
                                                        calendarStartDay={1}
                                                        showPopperArrow={false}
                                                        popperClassName="!z-[9999]"
                                                        popperPlacement="bottom-start"
                                                        popperModifiers={[
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 8]
                                                                }
                                                            },
                                                            {
                                                                name: 'preventOverflow',
                                                                options: {
                                                                    rootBoundary: 'viewport',
                                                                    tether: false,
                                                                    altAxis: true
                                                                }
                                                            }
                                                        ]}
                                                        renderCustomHeader={({
                                                            date,
                                                            decreaseMonth,
                                                            increaseMonth,
                                                            prevMonthButtonDisabled,
                                                            nextMonthButtonDisabled,
                                                        }) => (
                                                            <div className="flex items-center justify-between px-2 py-2">
                                                                <button
                                                                    onClick={decreaseMonth}
                                                                    disabled={prevMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng trước"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>

                                                                <span className="text-sm font-semibold text-gray-800">
                                                                    {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                                </span>

                                                                <button
                                                                    onClick={increaseMonth}
                                                                    disabled={nextMonthButtonDisabled}
                                                                    type="button"
                                                                    className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                                        nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                                    }`}
                                                                    aria-label="Tháng sau"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-5 w-5 text-gray-600"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isLoading}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                                    >
                                        Hủy
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                        {isLoading ? 'Đang xuất...' : 'Xuất file'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
